import React, { Component, useState, useEffect } from 'react';
import { compose } from '../../utils/compose';

import {
  AuthUserContext,
  withAuthorization,
  withEmailVerification,
} from '../Session';
import { withFirebase } from '../Firebase';
import { getDashboardRouteForEmail } from '../../utils/dashboardRouting';
import DashboardNav from '../Navigation/DashboardNav';
import ConfirmModal from '../ConfirmModal';
import './styles.css';

const AccountPageBase = ({ firebase }) => {
  return (
    <AuthUserContext.Consumer>
      {authUser => authUser ? <AccountContent authUser={authUser} firebase={firebase} /> : null}
    </AuthUserContext.Consumer>
  );
};

const AccountContent = ({ authUser, firebase }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [completedAgreement, setCompletedAgreement] = useState(null);
  const [showFranchiseeCard, setShowFranchiseeCard] = useState(false);
  const [cardGenerated, setCardGenerated] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [linkedRequest, setLinkedRequest] = useState(null);
  const dashboardRoute = getDashboardRouteForEmail(authUser?.email);
  
  // Get display name from Google account or username, fallback to email prefix
  const defaultName = authUser.displayName || authUser.username || (authUser.email ? authUser.email.split('@')[0] : 'User');
  const linkedUserId = linkedRequest?.userId || linkedRequest?.id || null;
  const linkedUserEmail = linkedRequest?.userEmail || linkedRequest?.email || null;
  const effectiveUserId = linkedUserId || authUser?.uid || null;
  const effectiveUserEmail = linkedUserEmail || authUser?.email || null;
  const franchiseIdSegment = (effectiveUserId?.substring(0, 10) || 'XXXXXXXXXX').toUpperCase();
  const fallbackFranchiseeId = `DM-${franchiseIdSegment}`;
  const normalizeFranchiseeId = (value, fallback) => {
    const raw = value ? String(value).trim() : '';
    if (!raw) {
      return fallback;
    }
    const upper = raw.toUpperCase();
    return upper.startsWith('DM-') ? upper : `DM-${upper}`;
  };
  const franchiseeIdDisplay = normalizeFranchiseeId(
    completedAgreement?.franchiseeId,
    fallbackFranchiseeId,
  );
  const franchiseeIdFilename = franchiseeIdDisplay.replace(/[^A-Z0-9-]/g, '');
  const cardSize = { width: 560, height: 340 };
  const cardTheme = {
    base: '#050505',
    surface: '#0c0c0c',
    border: 'rgba(255,255,255,0.14)',
    soft: 'rgba(255,255,255,0.08)',
    micro: 'rgba(255,255,255,0.55)',
    text: '#f8f8f8',
    muted: 'rgba(248,248,248,0.72)',
    faint: 'rgba(248,248,248,0.55)',
    shadow: '0 50px 120px rgba(0,0,0,0.85)',
    glow: 'rgba(255,255,255,0.06)',
    panel: 'rgba(255,255,255,0.02)',
    glass: 'rgba(255,255,255,0.06)',
  };
  
  // Form state with default values
  const getInitialFormData = () => ({
    fullName: authUser.fullName || defaultName,
    phoneNumber: authUser.phoneNumber || '',
    organization: authUser.organization || '',
    location: authUser.location || '',
    licenseNumber: authUser.licenseNumber || '',
    jobTitle: authUser.jobTitle || '',
    experience: authUser.experience || '',
    specialization: authUser.specialization || '',
    certifications: authUser.certifications || '',
  });
  
  const [formData, setFormData] = useState(getInitialFormData());
  const [originalFormData, setOriginalFormData] = useState(() => JSON.parse(JSON.stringify(getInitialFormData())));
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    confirmText: 'Yes',
    cancelText: 'No'
  });
  const resolveTimestamp = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };
  const formatCardDate = (date) =>
    date
      ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()
      : null;
  const cardIssuedAt = resolveTimestamp(completedAgreement?.franchiseeCardGeneratedAt);
  const cardIssuedDate = formatCardDate(cardIssuedAt);
  const cardValidThroughAt = cardIssuedAt ? new Date(cardIssuedAt.getTime()) : null;
  if (cardValidThroughAt) {
    cardValidThroughAt.setFullYear(cardValidThroughAt.getFullYear() + 5);
  }
  const cardValidThroughDate = formatCardDate(cardValidThroughAt);
  const cardValidityDisplay =
    cardIssuedDate && cardValidThroughDate ? (
      <>
        {cardIssuedDate}
        <br />
        <span style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '1px', color: cardTheme.faint }}>
          to
        </span>
        <br />
        {cardValidThroughDate}
      </>
    ) : (
      'PENDING'
    );

  // Check if there are unsaved changes
  const hasUnsavedChanges = isEditing && JSON.stringify(formData) !== JSON.stringify(originalFormData);

  // Debug: Log when unsaved changes state changes
  useEffect(() => {
    if (isEditing) {
      console.log('Profile form changed:', hasUnsavedChanges);
      console.log('Current form data:', formData);
      console.log('Original form data:', originalFormData);
    }
  }, [hasUnsavedChanges, formData, originalFormData, isEditing]);
  
  // Load and apply dark mode on mount
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    if (savedDarkMode) {
      document.body.classList.add('dark-mode');
    }
  }, []);

  useEffect(() => {
    if (!authUser?.email) {
      setLinkedRequest(null);
      return;
    }
    const requestQuery = firebase.db
      .collection('onboardingRequests')
      .where('assignedEmail', '==', authUser.email)
      .limit(1);

    const unsubscribe = requestQuery.onSnapshot(
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          setLinkedRequest({ id: doc.id, ...doc.data() });
        } else {
          setLinkedRequest(null);
        }
      },
      (error) => {
        console.error('Error loading linked onboarding request:', error);
        setLinkedRequest(null);
      },
    );

    return () => unsubscribe();
  }, [authUser?.email, firebase]);

  // Load completed agreement with payment
  useEffect(() => {
    if (!effectiveUserId) {
      setCompletedAgreement(null);
      setCardGenerated(false);
      return;
    }

    const unsubscribe = firebase.db
      .collection('franchiseAgreements')
      .where('userId', '==', effectiveUserId)
      .where('paymentCompleted', '==', true)
      .onSnapshot(
        (snapshot) => {
          if (!snapshot.empty) {
            const agreements = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            agreements.sort((a, b) => {
              const timeA = a.createdAt?.toMillis?.() || 0;
              const timeB = b.createdAt?.toMillis?.() || 0;
              return timeB - timeA;
            });
            const agreement = agreements[0];
            setCompletedAgreement(agreement);
            setCardGenerated(
              Boolean(agreement?.franchiseeCardGenerated || agreement?.franchiseeCardGeneratedAt),
            );
            return;
          }

          if (effectiveUserEmail) {
            firebase.db
              .collection('franchiseAgreements')
              .where('userEmail', '==', effectiveUserEmail)
              .where('paymentCompleted', '==', true)
              .get()
              .then((fallbackSnapshot) => {
                if (!fallbackSnapshot.empty) {
                  const agreements = fallbackSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                  }));
                  agreements.sort((a, b) => {
                    const timeA = a.createdAt?.toMillis?.() || 0;
                    const timeB = b.createdAt?.toMillis?.() || 0;
                    return timeB - timeA;
                  });
                  const agreement = agreements[0];
                  setCompletedAgreement(agreement);
                  setCardGenerated(
                    Boolean(agreement?.franchiseeCardGenerated || agreement?.franchiseeCardGeneratedAt),
                  );
                } else {
                  setCompletedAgreement(null);
                  setCardGenerated(false);
                }
              })
              .catch((error) => {
                console.error('Error loading agreement fallback:', error);
                setCompletedAgreement(null);
                setCardGenerated(false);
              });
          } else {
            setCompletedAgreement(null);
            setCardGenerated(false);
          }
        },
        (error) => {
          console.error('Error loading agreement:', error);
        }
      );

    return () => unsubscribe();
  }, [effectiveUserId, effectiveUserEmail, firebase]);

  useEffect(() => {
    if (isEditing) {
      return;
    }

    const agreementData = completedAgreement?.formData || {};
    const pickValue = (...values) => {
      for (const value of values) {
        if (value !== null && value !== undefined && String(value).trim() !== '') {
          return value;
        }
      }
      return '';
    };

    const mapped = {
      fullName: pickValue(agreementData.fullName, linkedRequest?.fullName),
      phoneNumber: pickValue(
        agreementData.phone,
        agreementData.mobile,
        linkedRequest?.phone,
      ),
      organization: pickValue(
        agreementData.organization,
        agreementData.entityType,
        agreementData.businessRegNumber,
      ),
      location: pickValue(
        agreementData.preferredLocation,
        agreementData.city,
        agreementData.addressLine1,
        agreementData.address,
      ),
      licenseNumber: pickValue(agreementData.reraNumber, agreementData.licenseNumber),
      jobTitle: pickValue(agreementData.jobTitle),
      experience: pickValue(agreementData.yearsInRealEstate, agreementData.experience),
      specialization: pickValue(agreementData.specialization),
      certifications: pickValue(agreementData.certifications),
    };

    setFormData((prev) => {
      const next = { ...prev };
      if (mapped.fullName && (!next.fullName || next.fullName === defaultName)) {
        next.fullName = mapped.fullName;
      }
      Object.keys(mapped).forEach((key) => {
        if (key === 'fullName') {
          return;
        }
        if (!next[key] && mapped[key]) {
          next[key] = mapped[key];
        }
      });
      setOriginalFormData(JSON.parse(JSON.stringify(next)));
      return next;
    });
  }, [completedAgreement, linkedRequest, isEditing]);

  // Prompt user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !allowNavigation) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Do you want to save them?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, allowNavigation]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      // Update user data in Firestore
      await firebase.user(authUser.uid).update(formData);
      
      // Update originalFormData to match current after successful save
      // Deep copy to ensure proper change detection
      setOriginalFormData(JSON.parse(JSON.stringify(formData)));
      
      setMessage('Profile updated successfully!');
      setIsEditing(false);
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
      
      return true; // Return success indicator
    } catch (error) {
      setMessage('Error updating profile: ' + error.message);
      return false; // Return failure indicator
    } finally {
      setSaving(false);
    }
  };

  const downloadFranchiseeCardPdf = async () => {
    const front = document.getElementById('franchisee-card-front');
    const back = document.getElementById('franchisee-card-back');

    if (!front || !back) {
      return;
    }

    const html2canvas = (await import('html2canvas')).default;
    const captureFace = async (face) => {
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.style.width = `${cardSize.width}px`;
      wrapper.style.height = `${cardSize.height}px`;
      wrapper.style.padding = '0';
      wrapper.style.margin = '0';
      wrapper.style.overflow = 'hidden';
      wrapper.style.pointerEvents = 'none';
      wrapper.style.background = 'transparent';
      wrapper.style.transform = 'none';
      wrapper.style.perspective = 'none';
      wrapper.style.zIndex = '9999';

      const clone = face.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.inset = '0';
      clone.style.transform = 'none';
      clone.style.backfaceVisibility = 'visible';

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      const canvas = await html2canvas(wrapper, {
        backgroundColor: null,
        scale: 3,
        logging: false,
      });

      wrapper.remove();
      return canvas;
    };

    const frontCanvas = await captureFace(front);
    const backCanvas = await captureFace(back);

    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [cardSize.width, cardSize.height],
    });

    pdf.addImage(frontCanvas, 'PNG', 0, 0, cardSize.width, cardSize.height);
    pdf.addPage([cardSize.width, cardSize.height], 'landscape');
    pdf.addImage(backCanvas, 'PNG', 0, 0, cardSize.width, cardSize.height);
    pdf.save(`Douglas-McCartney-Franchisee-Card-${franchiseeIdFilename || franchiseIdSegment}.pdf`);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setModalConfig({
        title: 'Discard Changes?',
        message: 'You have unsaved changes. Are you sure you want to discard them?',
        confirmText: 'Discard',
        cancelText: 'Keep Editing',
        onConfirm: () => {
          setShowConfirmModal(false); // Close modal first
          setFormData(JSON.parse(JSON.stringify(originalFormData))); // Deep copy
          setIsEditing(false);
          setMessage('');
        },
        onCancel: () => {
          setShowConfirmModal(false); // Just close modal and keep editing
        }
      });
      setShowConfirmModal(true);
    } else {
      setFormData(JSON.parse(JSON.stringify(originalFormData))); // Deep copy
      setIsEditing(false);
      setMessage('');
    }
  };

  const handleNavigateToDashboard = () => {
    if (hasUnsavedChanges) {
      setModalConfig({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes in your profile. Do you want to save them before leaving?',
        confirmText: 'Save & Leave',
        cancelText: 'Leave Without Saving',
        onConfirm: async () => {
          setShowConfirmModal(false); // Close modal first
          const saved = await handleSave();
          
          if (saved) {
            console.log('Profile saved successfully, navigating to dashboard');
            setAllowNavigation(true);
            setTimeout(() => {
              window.location.href = dashboardRoute;
            }, 300);
          } else {
            console.error('Failed to save profile');
            setAllowNavigation(false);
          }
        },
        onCancel: () => {
          setShowConfirmModal(false); // Close modal first
          setAllowNavigation(true);
          setTimeout(() => {
            window.location.href = dashboardRoute;
          }, 100);
        }
      });
      setShowConfirmModal(true);
    } else {
      window.location.href = dashboardRoute;
    }
  };

  const handleSignOut = () => {
    if (hasUnsavedChanges) {
      setModalConfig({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes in your profile. Do you want to save them before signing out?',
        confirmText: 'Save & Sign Out',
        cancelText: 'Sign Out Without Saving',
        onConfirm: async () => {
          setShowConfirmModal(false); // Close modal first
          const saved = await handleSave();
          
          if (saved) {
            console.log('Profile saved successfully, signing out');
            setAllowNavigation(true);
            setTimeout(() => {
              firebase.doSignOut();
            }, 300);
          } else {
            console.error('Failed to save profile');
            setAllowNavigation(false);
          }
        },
        onCancel: () => {
          setShowConfirmModal(false); // Close modal first
          setAllowNavigation(true);
          setTimeout(() => {
            firebase.doSignOut();
          }, 100);
        }
      });
      setShowConfirmModal(true);
    } else {
      firebase.doSignOut();
    }
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
  };

  const displayName =
    completedAgreement?.formData?.fullName ||
    linkedRequest?.fullName ||
    authUser.fullName ||
    defaultName;
  const avatarInitial = displayName.charAt(0).toUpperCase();
  
  return (
    <div className="account-page">
      {/* Navigation Bar */}
      <DashboardNav authUser={authUser} />
      
      <div className="account-container">
        {/* Profile Header */}
        <div className="account-header">
          <div className="profile-avatar-large">
            {avatarInitial}
          </div>
          <div className="profile-info">
            <h1 className="profile-name">
              {displayName}
            </h1>
            <p className="profile-email">{authUser.email || 'No email provided'}</p>
            <div className="profile-badges">
              <span className="badge badge-verified">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Verified Account
              </span>
              <span className="badge badge-role">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {authUser.roles && Object.keys(authUser.roles).length > 0 
                  ? Object.keys(authUser.roles)[0].toUpperCase() 
                  : 'MEMBER'}
              </span>
            </div>
          </div>
          <div className="profile-actions">
            {isEditing ? (
              <>
                <button 
                  className="btn-save-profile"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button 
                  className="btn-cancel-profile"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </button>
              </>
            ) : (
              <button 
                className="btn-edit-profile"
                onClick={() => setIsEditing(true)}
              >
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Success/Error Message */}
        {message && (
          <div className={`profile-message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        {/* Profile Details Grid */}
        <div className="account-section">
          <h2 className="section-title">Personal Information</h2>
          <div className="profile-details-grid">
            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Email Address</span>
                <span className="detail-value">
                  {completedAgreement?.formData?.email ||
                    linkedRequest?.userEmail ||
                    authUser.email}
                </span>
              </div>
            </div>

            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Full Name</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="Enter your full name"
                  />
                ) : (
                  <span className="detail-value">{formData.fullName || displayName}</span>
                )}
              </div>
            </div>

            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Phone Number</span>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="Enter your phone number"
                  />
                ) : (
                  <span className={`detail-value ${!formData.phoneNumber ? 'add-field' : ''}`}>
                    {formData.phoneNumber || 'Add Phone Number'}
                  </span>
                )}
              </div>
            </div>

            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Organization</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="Enter your organization"
                  />
                ) : (
                  <span className={`detail-value ${!formData.organization ? 'add-field' : ''}`}>
                    {formData.organization || 'Add Organization'}
                  </span>
                )}
              </div>
            </div>

            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Location</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="Enter your location"
                  />
                ) : (
                  <span className={`detail-value ${!formData.location ? 'add-field' : ''}`}>
                    {formData.location || 'Add Location'}
                  </span>
                )}
              </div>
            </div>

            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">License Number</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="Enter your license number"
                  />
                ) : (
                  <span className={`detail-value ${!formData.licenseNumber ? 'add-field' : ''}`}>
                    {formData.licenseNumber || 'Add License Number'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="account-section">
          <h2 className="section-title">Professional Information</h2>
          <div className="profile-details-grid">
            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Job Title</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="Enter your job title"
                  />
                ) : (
                  <span className={`detail-value ${!formData.jobTitle ? 'add-field' : ''}`}>
                    {formData.jobTitle || 'Add Job Title'}
                  </span>
                )}
              </div>
            </div>

            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Years of Experience</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="e.g., 5 years"
                  />
                ) : (
                  <span className={`detail-value ${!formData.experience ? 'add-field' : ''}`}>
                    {formData.experience || 'Add Experience'}
                  </span>
                )}
              </div>
            </div>

            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Specialization</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="Enter your specialization"
                  />
                ) : (
                  <span className={`detail-value ${!formData.specialization ? 'add-field' : ''}`}>
                    {formData.specialization || 'Add Specialization'}
                  </span>
                )}
              </div>
            </div>

            <div className="profile-detail-card">
              <div className="detail-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="detail-content">
                <span className="detail-label">Certifications</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="certifications"
                    value={formData.certifications}
                    onChange={handleInputChange}
                    className="detail-input"
                    placeholder="Enter your certifications"
                  />
                ) : (
                  <span className={`detail-value ${!formData.certifications ? 'add-field' : ''}`}>
                    {formData.certifications || 'Add Certifications'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div className="account-section">
          <h2 className="section-title">Your Statistics</h2>
          <div className="profile-stats">
            <div className="profile-stat">
              <div className="stat-number">{authUser.activeListings || '0'}</div>
              <div className="stat-label">Active Listings</div>
            </div>
            <div className="profile-stat">
              <div className="stat-number">{authUser.totalTransactions || '0'}</div>
              <div className="stat-label">Total Transactions</div>
            </div>
            <div className="profile-stat">
              <div className="stat-number">{authUser.portfolioValue || '₹0'}</div>
              <div className="stat-label">Portfolio Value</div>
            </div>
            <div className="profile-stat">
              <div className="stat-number">{authUser.clientRating || '0.0'}</div>
              <div className="stat-label">Client Rating</div>
            </div>
          </div>
        </div>

        {/* Franchisee Card Section */}
        {completedAgreement && (
          <div className="account-section" style={{ marginBottom: '30px' }}>
            <div style={{
              padding: '25px',
              background: 'linear-gradient(145deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.05) 100%)',
              border: '2px solid rgba(0,0,0,0.08)',
              borderRadius: 0
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <div>
                  <h2 className="section-title" style={{ margin: '0 0 8px 0' }}>
                    Your Franchisee ID Card
                  </h2>
                  <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                    Access your official Douglas & McCartney franchisee identification card
                  </p>
                </div>
              </div>
              
              <button
                onClick={async () => {
                  setShowFranchiseeCard(true);
                  
                  // Mark card as generated (only on first generation)
                  if (!cardGenerated && completedAgreement) {
                    try {
                      await firebase.db
                        .collection('franchiseAgreements')
                        .doc(completedAgreement.id)
                        .update({
                          franchiseeCardGenerated: true,
                          ...(completedAgreement?.franchiseeCardGeneratedAt
                            ? {}
                            : { franchiseeCardGeneratedAt: firebase.fieldValue.serverTimestamp() }),
                        });
                      setCardGenerated(true);
                    } catch (err) {
                      console.error('Error marking card as generated:', err);
                    }
                  }
                }}
                style={{
                  background: cardGenerated 
                    ? 'linear-gradient(145deg, #ffffff 0%, #e0e0e0 100%)'
                    : 'linear-gradient(145deg, #000 0%, #1a1a1a 100%)',
                  color: cardGenerated ? '#000' : '#fff',
                  padding: '16px 32px',
                  borderRadius: 0,
                  border: cardGenerated ? '2px solid #2a2a2a' : '2px solid #000',
                  fontSize: '15px',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                }}
              >
                {cardGenerated ? (
                  <>
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Your Franchisee Card
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Generate Your Franchisee Card
                  </>
                )}
              </button>

              {cardGenerated && cardIssuedDate && (
                <p style={{
                  marginTop: '15px',
                  fontSize: '12px',
                  color: '#888',
                  fontWeight: '600',
                  letterSpacing: '1px'
                }}>
                  CARD GENERATED: {cardIssuedDate}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="account-footer">
          <button 
            className="footer-button dashboard-button"
            onClick={handleNavigateToDashboard}
          >
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Go to Dashboard
          </button>
          <button 
            className="footer-button signout-button"
            onClick={handleSignOut}
          >
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={handleConfirmModalClose}
        onConfirm={modalConfig.onConfirm}
        onCancel={modalConfig.onCancel}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />

      {/* Franchisee Card Modal */}
      {showFranchiseeCard && completedAgreement && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease',
            padding: '20px'
          }}
          onClick={() => setShowFranchiseeCard(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              animation: 'slideUp 0.3s ease'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowFranchiseeCard(false)}
              style={{
                position: 'absolute',
                top: '-15px',
                right: '-15px',
                width: '40px',
                height: '40px',
                borderRadius: 0,
                background: '#1a1a1a',
                color: 'white',
                border: '2px solid #333',
                fontSize: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                zIndex: 1,
                transition: 'all 0.3s ease',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#000';
                e.target.style.borderColor = '#555';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#1a1a1a';
                e.target.style.borderColor = '#333';
              }}
              aria-label="Close card"
            >
              X
            </button>

            {/* Font Import */}
            <link
              href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Inter:wght@400;500;600;700;800&display=swap"
              rel="stylesheet"
            />

            {/* Premium ID Card */}
            <div
              id="franchisee-card"
              style={{
                width: 'min(560px, 92vw)',
                aspectRatio: '28 / 17',
                height: 'auto',
                perspective: '2000px',
                cursor: 'pointer'
              }}
              onMouseEnter={() => setIsCardHovered(true)}
              onMouseLeave={() => setIsCardHovered(false)}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 1s cubic-bezier(0.23, 1, 0.32, 1)',
                  transform: isCardHovered ? 'rotateY(180deg)' : 'rotateY(0deg)'
                }}
              >
                {/* Front Face */}
                <div
                  id="franchisee-card-front"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '26px',
                    padding: 'clamp(22px, 4vw, 36px) clamp(24px, 4.5vw, 42px)',
                    background: `linear-gradient(145deg, ${cardTheme.base} 0%, ${cardTheme.surface} 45%, ${cardTheme.base} 100%)`,
                    boxShadow: cardTheme.shadow,
                    border: `1px solid ${cardTheme.border}`,
                    overflow: 'hidden',
                    color: cardTheme.text,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    backfaceVisibility: 'hidden'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `radial-gradient(circle at 20% 20%, ${cardTheme.glow}, transparent 52%), linear-gradient(115deg, ${cardTheme.soft} 0%, transparent 50%)`,
                      opacity: 0.85,
                      pointerEvents: 'none'
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: `linear-gradient(90deg, ${cardTheme.soft} 1px, transparent 1px), linear-gradient(180deg, ${cardTheme.soft} 1px, transparent 1px)`,
                      backgroundSize: '32px 32px',
                      opacity: 0.2,
                      pointerEvents: 'none'
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '-40%',
                      right: '-10%',
                      width: '60%',
                      height: '200%',
                      background: `linear-gradient(120deg, ${cardTheme.soft}, transparent 60%)`,
                      opacity: 0.55,
                      transform: 'rotate(12deg)'
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      inset: '18px',
                      borderRadius: '20px',
                      border: `1px solid ${cardTheme.soft}`,
                      opacity: 0.45,
                      pointerEvents: 'none'
                    }}
                  ></div>

                  <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', transform: 'translateY(-26px)' }}>
                    <div style={{ marginTop: 0 }}>
                      <p
                        style={{
                          margin: '10px 0 0 0',
                          fontSize: 'clamp(22px, 2.6vw, 30px)',
                          fontWeight: 500,
                          letterSpacing: '1.5px',
                          fontFamily: "'Cormorant Garamond', serif",
                          textTransform: 'capitalize',
                          transform: 'translateY(-4px)'
                        }}
                      >
                        {completedAgreement?.formData?.fullName || linkedRequest?.fullName || authUser?.displayName || 'Franchisee Name'}
                      </p>
                      <p style={{ margin: '6px 0 0 0', letterSpacing: '4px', fontSize: '10px', color: cardTheme.faint, textTransform: 'uppercase', transform: 'translateY(-4px)' }}>
                        EXCLUSIVE PARTNER - DOUGLAS & McCARTNEY
                      </p>
                    </div>

                    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                          gap: '12px'
                        }}
                      >
                        {[
                          { label: 'FRANCHISEE ID', value: franchiseeIdDisplay },
                          { label: 'TERRITORY', value: completedAgreement?.formData?.preferredLocation || completedAgreement?.formData?.city || 'India' },
                          { label: 'RERA NO.', value: completedAgreement?.formData?.reraNumber || 'N/A' },
                          { label: 'VALIDITY', value: cardValidityDisplay }
                        ].map((item) => (
                          <div
                            key={item.label}
                            style={{
                              border: `1px solid ${cardTheme.soft}`,
                              padding: '10px 12px',
                              borderRadius: '12px',
                              background: cardTheme.panel
                            }}
                          >
                            <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>{item.label}</p>
                            <p style={{ margin: '6px 0 0 0', fontSize: '12px', fontWeight: 600, lineHeight: 1.2, wordBreak: 'break-word' }}>{item.value}</p>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>CONTACT</p>
                          <p style={{ margin: 0, fontSize: '12px', color: cardTheme.muted }}>
                            {completedAgreement?.formData?.email || linkedRequest?.userEmail || authUser?.email}
                          </p>
                          <p style={{ margin: 0, fontSize: '12px', color: cardTheme.muted }}>
                            {completedAgreement?.formData?.phone || completedAgreement?.formData?.mobile || linkedRequest?.phone || 'N/A'}
                          </p>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', width: '100%' }}>
                        <div style={{ textAlign: 'right' }}>
                          <p style={{ margin: 0, fontSize: '9px', letterSpacing: '2px', color: cardTheme.faint }}>ISSUED BY</p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '10px', letterSpacing: '2px', color: cardTheme.text }}>DOUGLAS & McCARTNEY BOARD</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back Face */}
                <div
                  id="franchisee-card-back"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '26px',
                    background: `linear-gradient(145deg, ${cardTheme.base} 0%, ${cardTheme.surface} 50%, ${cardTheme.base} 100%)`,
                    border: `1px solid ${cardTheme.border}`,
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                    overflow: 'hidden',
                    boxShadow: cardTheme.shadow,
                    color: cardTheme.text
                  }}
                >
                  <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle at 30% 30%, ${cardTheme.glow}, transparent 60%), radial-gradient(circle at 70% 70%, ${cardTheme.soft}, transparent 60%)`, opacity: 0.5 }}></div>
                  <div style={{ position: 'absolute', inset: '18px', borderRadius: '20px', border: `1px solid ${cardTheme.soft}`, opacity: 0.35 }}></div>
                  <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src="/Douglas2-removebg-preview.png"
                      alt="Douglas & McCartney"
                      style={{ width: '200px', height: '200px', objectFit: 'contain', filter: 'brightness(1.1)' }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {cardGenerated && cardIssuedDate && (
              <div style={{
                marginTop: '20px',
                padding: '14px 24px',
                background: cardTheme.panel,
                border: `1px solid ${cardTheme.soft}`,
                borderRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '12px',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: cardTheme.muted
              }}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: cardTheme.text }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span style={{ fontWeight: '600', color: cardTheme.text }}>
                  OFFICIAL FRANCHISEE CARD - GENERATED ON {cardIssuedDate}
                </span>
              </div>
            )}

            <button
              onClick={downloadFranchiseeCardPdf}
              style={{
                marginTop: '20px',
                width: '100%',
                padding: '18px',
                background: '#f8f8f8',
                color: '#0a0a0a',
                border: '1px solid #f8f8f8',
                borderRadius: 0,
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '3px',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#0a0a0a';
                e.currentTarget.style.color = '#f8f8f8';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 14px 40px rgba(0, 0, 0, 0.6)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#f8f8f8';
                e.currentTarget.style.color = '#0a0a0a';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.45)';
              }}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Download Franchisee Card PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const condition = authUser => !!authUser;

export default compose(
  withEmailVerification,
  withAuthorization(condition),
  withFirebase,
)(AccountPageBase);

